import React, { useState } from "react";
import {
  CHART_DRAWING_COLOR_OPTIONS,
  CHART_DRAWING_ROLE_OPTIONS,
  drawingRolePatch,
} from "./ChartDrawingInspector";
import ChartDrawingCoordinates from "./ChartDrawingCoordinateEditor";
import { drawingPriceSummary } from "./chartDrawingCoordinates";

export default function ChartDrawingToolbar({
  overlay,
  palette,
  busy,
  tickSize,
  onChange,
  onDetails,
  onClose,
  planContext,
}) {
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const role = overlay.semantic_role || "note";
  const style = {
    height: 34,
    border: `1px solid ${palette.border}`,
    borderRadius: 7,
    background: palette.background,
    color: palette.text,
    font: "inherit",
    fontSize: 12,
    padding: "0 8px",
    cursor: "pointer",
    minWidth: 0,
  };
  const change = async (patch) => {
    setSaving(true);
    setError(null);
    try {
      if ((await onChange(patch)) === false)
        setError("This drawing could not be saved.");
    } catch (_error) {
      setError("This drawing could not be saved.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      data-chart-drawing-compact-toolbar
      style={{ display: "flex", flexDirection: "column", gap: 6 }}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 6,
        }}
      >
        {overlay.kind === "horizontal_line" ? (
          <ChartDrawingCoordinates
            compact
            overlay={overlay}
            tickSize={tickSize}
            onChange={onChange}
            palette={palette}
            busy={busy || saving}
          />
        ) : (
          <button
            type="button"
            style={{
              ...style,
              maxWidth: 190,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={drawingPriceSummary(overlay)}
            onClick={() => onDetails("coordinates")}
          >
            {drawingPriceSummary(overlay)}
          </button>
        )}
        <select
          data-chart-drawing-role-select
          aria-label="Drawing role"
          value={role}
          disabled={busy || saving || overlay.locked}
          onChange={(event) =>
            void change(drawingRolePatch(overlay, event.target.value))
          }
          style={{ ...style, maxWidth: 124 }}
        >
          {CHART_DRAWING_ROLE_OPTIONS.map(([value, label]) => (
            <option value={value} key={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          data-chart-drawing-quick-color
          aria-label="Drawing color"
          title="Drawing color"
          value={overlay.color || "#60a5fa"}
          disabled={busy || saving || overlay.locked}
          onChange={(event) => void change({ color: event.target.value })}
          style={{
            ...style,
            width: 34,
            padding: 2,
            color: overlay.color || "#60a5fa",
            fontSize: 18,
          }}
        >
          {CHART_DRAWING_COLOR_OPTIONS.map(([value, label]) => (
            <option value={value} key={value} aria-label={label}>
              ● {label}
            </option>
          ))}
          {overlay.color &&
          !CHART_DRAWING_COLOR_OPTIONS.some(
            ([color]) => color === overlay.color,
          ) ? (
            <option value={overlay.color}>● Custom</option>
          ) : null}
        </select>
        <button
          type="button"
          data-chart-drawing-details
          onClick={() => onDetails("style")}
          style={style}
        >
          Details
        </button>
        <button
          type="button"
          aria-label="Deselect drawing"
          onClick={onClose}
          style={{ ...style, border: 0, padding: "0 4px", fontSize: 18 }}
        >
          ×
        </button>
      </div>
      {planContext?.linkedCondition ? (
        <div
          data-chart-drawing-plan-comparison
          style={{ fontSize: 11, color: palette.mutedText, lineHeight: 1.4 }}
        >
          Drawing: <strong>{drawingPriceSummary(overlay)}</strong> · Saved plan:{" "}
          <strong>
            {drawingPriceSummary(planContext.linkedCondition.definition)}
          </strong>
          {!planContext.linkCurrent ? (
            <button
              type="button"
              onClick={() => onDetails("plan")}
              style={{ ...style, height: 28, marginLeft: 6, color: "#2563eb" }}
            >
              Review change
            </button>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <div role="alert" style={{ color: "#ef4444", fontSize: 11 }}>
          {error}
        </div>
      ) : null}
    </div>
  );
}
